/*
 * Hiring v2 (owner, 2026-11-04: "yes build all"): the stages, why someone is not a fit, and every text an applicant gets,
 * in English or Spanish. Most hourly applicants never read email and take the first job that texts back, so the careers
 * page texts a link to pick a phone screen time, reminds them, and closes out kindly. Research and plan in the vault
 * (project-hiring-pipeline). The crew are 1099 today (owner), so paperwork asks for a W-9, not a W-4 or an I-9.
 */
import { z } from "zod";

export const APPLY_LANGS = ["en", "es"] as const;
export type ApplyLang = (typeof APPLY_LANGS)[number];

/** Why someone left the board, for the reports. Never their language, age or where they are from. */
export const NO_FIT_REASONS = ["no_answer", "no_show", "schedule", "no_ride", "pay", "background", "other"] as const;
export type NoFitReason = (typeof NO_FIT_REASONS)[number];
export const NO_FIT_WORDS: Record<NoFitReason, string> = {
  no_answer: "Never answered",
  no_show: "Did not show",
  schedule: "Days do not fit",
  no_ride: "No ride",
  pay: "Pay did not fit",
  background: "Background check",
  other: "Other",
};

/** The phone screen scorecard, each 1 to 5, so everyone rates the same way. */
export const SCREEN_SCORES = ["onTime", "talks", "experience", "days", "hire"] as const;
export type ScreenScore = (typeof SCREEN_SCORES)[number];
export const SCREEN_SCORE_WORDS: Record<ScreenScore, string> = {
  onTime: "Picked up on time",
  talks: "Easy to talk to",
  experience: "Cleaning or crew work before",
  days: "Days and hours fit",
  hire: "Would you hire them",
};
export const screenScores = z.partialRecord(z.enum(SCREEN_SCORES), z.number().int().min(1).max(5));
export type ScreenScores = z.infer<typeof screenScores>;

/** Weekdays, 1 Monday to 7 Sunday, in each language. */
export const WEEKDAY_WORDS: Record<ApplyLang, readonly string[]> = {
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  es: ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"],
};

/** A reply that means yes: Y, yes, S, si, ok. */
export function saysYes(text: string): boolean {
  return /^\s*(y|ye[sp]|yeah|s|s[ií]|ok(ay)?|claro)\b[\s.!]*$/i.test(text);
}

export type ApplicantTextKind = "apply" | "booked" | "remindDay" | "remindSoon" | "confirmed" | "notNow" | "noFit" | "pool";

export interface ApplicantTextVars {
  first: string;
  brand: string;
  /** The page to pick or change the phone screen time. */
  link?: string;
  /** When we call, like "Tuesday, Nov 10 at 2:00 PM". */
  when?: string;
  /** Just the time, like "2:00 PM". */
  time?: string;
}

/** Every text an applicant gets. Plain words, no colons, one ask each. */
export function applicantText(kind: ApplicantTextKind, lang: ApplyLang, v: ApplicantTextVars): string {
  const first = v.first.trim() || (lang === "es" ? "hola" : "there");
  const link = v.link ?? "";
  if (lang === "es")
    switch (kind) {
      case "apply":
        return `Hola ${first}, gracias por aplicar a ${v.brand}. Escoge una hora para una llamada corta aquí ${link}`;
      case "booked":
        return `¡Listo! ${v.brand} te llama ${v.when ?? ""}.`;
      case "remindDay":
        return `Hola ${first}, ${v.brand} te llama mañana a las ${v.time ?? ""}. Responde S para confirmar, o escoge otra hora aquí ${link}`;
      case "remindSoon":
        return `${v.brand} te llama hoy a las ${v.time ?? ""}. Responde S para confirmar.`;
      case "confirmed":
        return "¡Gracias, hablamos pronto!";
      case "notNow":
        return `Gracias por aplicar, ${first}. Ahora no tenemos un puesto que te quede bien, pero te escribimos cuando lo tengamos.`;
      case "noFit":
        return `Gracias por aplicar, ${first}. Por ahora escogimos a otra persona. ¡Te deseamos lo mejor!`;
      case "pool":
        return `Hola ${first}, somos ${v.brand}. Estamos contratando otra vez. ¿Todavía te interesa? Responde S.`;
    }
  switch (kind) {
    case "apply":
      return `Hi ${first}, thanks for applying to ${v.brand}! Pick a time for a quick phone call here ${link}`;
    case "booked":
      return `You're all set! ${v.brand} will call you ${v.when ?? ""}.`;
    case "remindDay":
      return `Hi ${first}, ${v.brand} will call you tomorrow at ${v.time ?? ""}. Reply Y to confirm, or pick a new time here ${link}`;
    case "remindSoon":
      return `${v.brand} will call you today at ${v.time ?? ""}. Reply Y to confirm.`;
    case "confirmed":
      return "Thanks, talk soon!";
    case "notNow":
      return `Thanks for applying, ${first}. We don't have a spot that fits right now, but we'll text you when we do.`;
    case "noFit":
      return `Thanks for applying, ${first}. We went another way for now. We wish you the best!`;
    case "pool":
      return `Hi ${first}, it's ${v.brand}. We're hiring again. Still interested? Reply Y.`;
  }
}

/** The words next to the texts box on the careers page, saved as the proof of the yes. */
export function textConsentWords(lang: ApplyLang, brand: string): string {
  return lang === "es"
    ? `Acepto mensajes de texto de ${brand} sobre mi solicitud a este número. Pueden aplicar tarifas de mensajes y datos. Responde STOP para parar.`
    : `I agree to get texts from ${brand} about my application at this number. Msg and data rates may apply. Reply STOP to stop.`;
}

/** The roles a brand hires for when the owner has not set any, by trade. Trial pay is set per role in Hiring settings. */
export function defaultRoles(trade: string): { id: string; name: string; nameEs: string; trialRateCents: number }[] {
  if (trade === "lawn") return [{ id: "crew", name: "Lawn crew", nameEs: "Jardinero", trialRateCents: 0 }];
  if (trade === "washers") return [{ id: "tech", name: "Washer tech", nameEs: "Técnico de lavado", trialRateCents: 0 }];
  return [{ id: "cleaner", name: "House cleaner", nameEs: "Limpiador o limpiadora", trialRateCents: 0 }];
}
